import {
	IonFab,
	IonFabButton,
	IonHeader,
	IonIcon,
	IonRefresher,
	IonRefresherContent,
	useIonRouter,
	useIonViewWillEnter,
} from "@ionic/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import Image_nl from "@/assets/nl/perfect-day-image-generation-failed-small.png";
import { Button } from "@/components/Global/Button";
import { Content } from "@/components/Global/Content";
import { backendService } from "@/lib/backend";
import { plusIcon } from "@/lib/icons/@heroicons/react/16/solid";
import { queryOptions_perfectDayScenes } from "@/lib/query/functions/perfect-day/scenes";
import { Routes } from "@/lib/router";
import { PerfectDaySceneCard } from "./Card";
import { PerfectDayHeader } from "./OverviewHeader";
import { PerfectDaySceneReorderContent } from "./Reorder/Content";

export const PerfectDaySceneOverviewContent = () => {
	const [isReorderMode, setIsReorderMode] = useState(false);
	const { t, i18n } = useTranslation();

	const queryPerfectDayScenes = useQuery(queryOptions_perfectDayScenes());
	useIonViewWillEnter(() => {
		queryPerfectDayScenes.refetch();
	});

	const router = useIonRouter();

	async function handleNewScene() {
		const lastScene = queryPerfectDayScenes.data?.[queryPerfectDayScenes.data.length - 1];
		if (lastScene?.chat.status === "IMAGE_GENERATION" || lastScene?.chat.status === "REFLECTION") {
			router.push(Routes.PerfectDayChat({ id: lastScene.id }));
		} else {
			const response = await backendService.perfectDay.perfectDayControllerCreateScene();
			router.push(Routes.PerfectDayChat({ id: response.data.id }));
		}
	}

	const completedScenes =
		queryPerfectDayScenes.data?.filter((scene) => scene.chat.status === "COMPLETED") ?? [];

	return (
		<>
			{!isReorderMode ? (
				<>
					<IonHeader>
						<PerfectDayHeader setIsReorderMode={setIsReorderMode} />
					</IonHeader>
					<Content className="part-scroll">
						<IonRefresher
							slot="fixed"
							onIonRefresh={(event) =>
								queryPerfectDayScenes.refetch().then(() => event.detail.complete())
							}
						>
							<IonRefresherContent />
						</IonRefresher>

						<IonHeader collapse="condense" className="-mt-(--padding-top)">
							<PerfectDayHeader isCondensed setIsReorderMode={setIsReorderMode} />
						</IonHeader>

						<div className="flex flex-col gap-4">
							{completedScenes.map((scene) => (
								<PerfectDaySceneCard
									key={scene.id}
									className="flex flex-col gap-4 p-0"
									onClick={() => router.push(Routes.PerfectDayDetails({ id: scene.id }))}
								>
									<div className="relative aspect-19/9 w-full">
										<img
											src={{ nl: scene.imageUrl || Image_nl }[i18n.language]}
											alt={t("perfect-day.details.image-alt")}
											className="absolute inset-0 h-full w-full rounded-2xl object-cover object-center"
										/>
									</div>
									<div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 bg-linear-to-t from-black/80 to-transparent px-4 pt-6 pb-4">
										<strong className="text-brown-100 text-2xl font-bold">{scene.name}</strong>
									</div>
								</PerfectDaySceneCard>
							))}
						</div>
						<Button
							data-attr="page.perfect-day.create"
							variant="secondary"
							onClick={handleNewScene}
						>
							{t("perfect-day.create")}
						</Button>

						<IonFab slot="fixed" vertical="bottom" horizontal="end">
							<IonFabButton onClick={handleNewScene}>
								<IonIcon icon={plusIcon} className="ml-[0.04em] text-2xl" />
							</IonFabButton>
						</IonFab>
					</Content>
				</>
			) : (
				<PerfectDaySceneReorderContent
					sceneData={completedScenes}
					onCompleted={() => {
						queryPerfectDayScenes.refetch();
						setIsReorderMode(false);
					}}
				/>
			)}
		</>
	);
};
